import { requestApi } from '@app/api';
import { withDocument } from '@app/api/paths';
import { DOCUMENT_MAX_ATTACHMENTS_COUNT } from '@app/features/apply-react-signing/components/FieldSetupStep/ParticipantFieldStatusBoard/AttachmentSettingModal';
import { RequesterAttachment } from '@app/store/documentSetup/RequesterAttachment';
import { Attachment as TransportLayerAttachment } from '@app/store/transport-layer/types/attachment';
import { ParticipantWithSigningProperties } from '@app/store/transport-layer/types/participant';
import { WithId } from '@app/store/transport-layer/types/utility';

import { getDocumentsParticipants } from '../participants';

interface Attachment {
  attachmentId: string;
  documentId: string;
  file: {
    downloadUrl?: string;
    extension: string;
    name: string;
    size: number;
  } | null;
  participant: {
    contactInfo: string;
    name: string;
    participantId: string;
    signingOrder: number;
  };
  request: {
    attachmentType: string;
    message: string | null;
    required: boolean;
  };
}

export interface DocumentsAttachmentsResponse {
  result: {
    attachments: Attachment[];
    requesterAttachments: RequesterAttachment[];
  };
}
export interface NewDocumentsAttachmentsResponse {
  count: number;
  attachments: WithId<TransportLayerAttachment['ToReceive']>[];
}

const convertToDeprecatedAttachmentsResponse = (
  { attachments }: NewDocumentsAttachmentsResponse,
  participants: WithId<ParticipantWithSigningProperties>[]
): DocumentsAttachmentsResponse => ({
  result: {
    attachments: attachments.flatMap<Attachment>((attachment) => {
      const participant = participants.find(
        (p) => p.dataLabel === attachment.participant.dataLabel
      );

      if (participant?.type === 'SIGNER') {
        return [
          {
            attachmentId: attachment.id,
            documentId: attachment.documentId as string,
            file:
              (attachment.input?.file && {
                extension: attachment.input.file.extension,
                name: attachment.input.file.name,
                size: attachment.input.file.size,
                downloadUrl: attachment.input.file.url,
              }) ||
              null,
            participant: {
              contactInfo: participant?.contact || '',
              name: participant?.name || '',
              participantId: participant?.id || '',
              signingOrder: participant?.order || 0,
            },
            request: {
              attachmentType: attachment.title,
              message: attachment.message,
              required: attachment.config.required,
            },
          },
        ];
      }

      return [];
    }),
    requesterAttachments: attachments.flatMap<RequesterAttachment>((a) => {
      const participant = participants.find((p) => p.dataLabel === a.participant.dataLabel);

      if (participant?.type === 'REQUESTER') {
        return [
          {
            documentId: a.documentId as string,
            // 요청자 첨부파일에는 항상 업로드된 파일이 있습니다.
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            file: a.input!.file!,
            id: a.id,
            participantName: participant.name || '',
          },
        ];
      }

      return [];
    }),
  },
});

export const getNewDocumentsAttachments = ({
  workspaceId,
  documentId,
  participantId,
}: {
  workspaceId: string;
  documentId: string;
  participantId?: string;
}) => {
  return requestApi<NewDocumentsAttachmentsResponse>({
    url: withDocument(`/workspaces/${workspaceId}/documents/${documentId}/attachments`),
    method: 'GET',
    credentials: true,
    params: participantId
      ? { participant_id: participantId, limit: DOCUMENT_MAX_ATTACHMENTS_COUNT }
      : { limit: DOCUMENT_MAX_ATTACHMENTS_COUNT },
  });
};

/**
 * GET 첨부파일 목록 조회
 * @deprecated Endpoint response is modified in standardize-document-content
 * @see https://www.notion.so/modusign/bb8e24e723434ecc946dfecb85db5ee3#b1d05f141e8446c088310ffa63e18a5a
 */
// eslint-disable-next-line import/prefer-default-export
export function getDocumentsAttachmentsDeprecated({
  workspaceId,
  documentId,
  participantId,
}: {
  workspaceId: string;
  documentId: string;
  participantId?: string;
}): Promise<{ attachments: Attachment[]; requesterAttachments: RequesterAttachment[] }> {
  return Promise.all([
    getNewDocumentsAttachments({ workspaceId, documentId, participantId }),
    getDocumentsParticipants({ workspaceId, documentId }),
  ]).then((responses) => {
    return convertToDeprecatedAttachmentsResponse(responses[0].data, responses[1].participants)
      .result;
  });
}

export function getDocumentsAttachments({
  documentId,
  participantId,
  workspaceId,
}: {
  documentId: string;
  participantId?: string;
  workspaceId: string;
}): Promise<NewDocumentsAttachmentsResponse> {
  return requestApi<NewDocumentsAttachmentsResponse>({
    url: withDocument(`/workspaces/${workspaceId}/documents/${documentId}/attachments`),
    method: 'GET',
    credentials: true,
    params: participantId
      ? { participant_id: participantId, limit: DOCUMENT_MAX_ATTACHMENTS_COUNT }
      : { limit: DOCUMENT_MAX_ATTACHMENTS_COUNT }, // 문서의 모든 첨부파일을 조회하기 위해 limit을 DOCUMENT_MAX_ATTACHMENTS_COUNT으로 설정
  }).then(({ data }) => data);
}

/**
 * GET 첨부파일 조회
 *
 * Endpoint is created in standardize-document-content
 * @see https://www.notion.so/modusign/bb8e24e723434ecc946dfecb85db5ee3#37a98c3f8753458980df208999d7a55d
 */
export function getDocumentsAttachment({
  workspaceId,
  documentId,
  attachmentId,
}: {
  workspaceId: string;
  documentId: string;
  attachmentId: string;
}): Promise<WithId<TransportLayerAttachment['ToReceive']>> {
  return requestApi<WithId<TransportLayerAttachment['ToReceive']>>({
    url: withDocument(
      `/workspaces/${workspaceId}/documents/${documentId}/attachments/${attachmentId}`
    ),
    method: 'GET',
    credentials: true,
  }).then((response) => response.data);
}
